import { Component, ViewEncapsulation, input, inject, model } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { noop } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';

import { ApContentService } from '@ap/content';
import { ErrorMessages, ApFormErrorsComponent } from '@ap/form/errors';
import { ApIconComponent } from '@ap/icon';
import { HelpText, HelpTextPosition } from '@ap/form';

import { DropdownOption } from './dropdown.model';
import content from './dropdown.content.json';

/**
 * @ngdoc component
 * @name ApDropdownComponent
 * @description The dropdown component allows the user to select an option from a dropdown.
 */
@Component({
    selector: 'ap-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApIconComponent, ApFormErrorsComponent, NgTemplateOutlet]
})
export class ApDropdownComponent implements ControlValueAccessor, HelpText {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly contentService = inject(ApContentService);

    /**
     * Field label. Shown in the field. Moves up if focussed, filled in or a prefix is set
     */
    public readonly label = input.required<string>();

    /**
     * Dropdown options. List of strings
     */
    public readonly options = input.required<string[] | DropdownOption[]>();

    /**
     * Description of field. Shown above the field
     */
    public readonly description = input<string>();

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages | string[]>();

    /**
     * Help text shown under field. Disappears if errors are present
     */
    public readonly helpText = input<string>();

    /**
     * Help text position, either 'below' (default) or 'above' the input field.
     */
    public readonly helpTextPosition = model<HelpTextPosition>(HelpTextPosition.BELOW);

    /**
     * Expose `HelpTextPosition` enum for template
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected readonly HelpTextPosition = HelpTextPosition;

    /**
     * Native html autocomplete value. For a complete list see: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/autocomplete#values
     * This uses the [native browser autocomplete](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/autocomplete) which autofills forms based on information from your browser.
     */
    public readonly autocomplete = input('off', {
        transform: (value: string): string => {
            return !value || value.length === 0 ? 'off' : value;
        }
    });

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `dropdown-${Math.round(Math.random() * 10000000)}`;

    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    constructor() {
        this.control && (this.control.valueAccessor = this);
        this.contentService.addContentItems(content);
        this.content = content;
    }

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    private _value = null;

    public get value(): string {
        return this._value;
    }

    public set value(val: string) {
        const options = this.options();
        if (typeof options[0] === 'string') {
            const valIsOption = (options as string[]).indexOf(val) > -1;
            this._value = valIsOption ? val : null;
            return;
        }
        const valIsOption = (options as DropdownOption[]).find((option) => option.value === val);
        this._value = valIsOption ? val : null;
    }

    /** @internal */
    public isDisabled = false;

    /**
     * Set value if select changes
     */
    protected onDropdownChange(event: Event): void {
        this.value = (event.target as HTMLInputElement).value;
        this.onChange(this.value);
        this.onTouched();
    }

    /**
     * Set touched and dirty state if select blurs
     */
    protected onDropdownBlur(): void {
        this.onTouched();
        if (this.control?.control) {
            this.control.control.markAsDirty();
        }
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Control value accessor function
     * Sets isDisabled state
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /**
     * Control value accessor function
     * Write value
     * @internal
     */
    public writeValue(value: string) {
        this.value = value;
    }

    /**
     * Aria described by contains conditional fields that describe the select field
     * */
    protected get ariaDescribedBy(): string | null {
        const ids = [
            ...(this.description() ? [`${this.uniqueId}-description`] : []),
            ...(this.control ? [`${this.uniqueId}-errors`] : []),
            ...(this.helpText() ? [`${this.uniqueId}-help-text`] : [])
        ].join(' ');

        return ids.length > 0 ? ids : null;
    }
}
